import {
  TechnologyContributorCreateDto,
  TechnologyContributorSelectDto,
  TechnologyContributorUpdateDto,
  TechnologyContributorsSelectDto,
} from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { useGetUserSelectedCompanyId, useRoleEndpointPrefix } from '@queries/users.query';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useTechnologyContributorsQueryOptions = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const rolePrefix = useRoleEndpointPrefix();

  // rolePrefix is null when user is not loaded or has no valid role
  // Innovators require selectedCompanyId, other roles don't
  const enabled = Boolean(
    technologyId && isAuthenticated && rolePrefix && (rolePrefix === 'innovator' ? Boolean(selectedCompanyId) : true),
  );

  return queryOptions<TechnologyContributorSelectDto[], ApiErrorWrapper>({
    enabled,
    queryKey:
      enabled && technologyId ? queryKeys.technology.getContributors(technologyId) : queryKeys.placeholders.default,
    queryFn: async () => {
      if (!technologyId || !rolePrefix) return [];
      const result = await apiGet<TechnologyContributorsSelectDto>({
        path: `/v1/${rolePrefix}/technology-contributors/${technologyId}`,
      });
      return result.contributors;
    },
  });
};

export const useGetTechnologyContributors = (technologyId?: string) => {
  const contributorsQueryOptions = useTechnologyContributorsQueryOptions(technologyId);
  return useQuery(contributorsQueryOptions);
};

export const useInnovatorAddTechnologyContributor = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    TechnologyContributorSelectDto,
    ApiErrorWrapper,
    TechnologyContributorCreateDto & { technologyId: string }
  >({
    mutationFn: async (data) => {
      const { technologyId, ...body } = data;
      return await apiPost<TechnologyContributorSelectDto>({
        path: `/v1/innovator/technology-contributors/${technologyId}`,
        body,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getContributors(variables.technologyId),
      });
    },
  });
};

export const useInnovatorUpdateTechnologyContributor = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    TechnologyContributorSelectDto,
    ApiErrorWrapper,
    TechnologyContributorUpdateDto & { contributorId: string; technologyId: string }
  >({
    mutationFn: async (data) => {
      const { contributorId, technologyId, ...body } = data;
      return await apiPatch<TechnologyContributorSelectDto>({
        path: `/v1/innovator/technology-contributors/${technologyId}/${contributorId}`,
        body,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getContributors(variables.technologyId),
      });
    },
  });
};

export const useInnovatorRemoveTechnologyContributor = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorWrapper, { contributorId: string; technologyId: string }>({
    mutationFn: async (data) => {
      const { contributorId, technologyId } = data;
      await apiDelete({
        path: `/v1/innovator/technology-contributors/${technologyId}/${contributorId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getContributors(variables.technologyId),
      });
    },
  });
};
